import { Box3, Group, MathUtils, Mesh, Object3D, Quaternion, Raycaster, Vector3 } from "three";
import * as THREE from "three";
import { OBB } from "assets/JS/OBB";
import { checkBVHCollision, checkCollision, checkCollisionWalls } from "./Collision";
import { AssetType, CollisionType } from "./enums";
import { GraphicService } from "./GraphicService";
import { ITransform } from "./interfaces";
import { Robot } from "./Robot";
import { Room } from "./Room";
import { moveBSU } from "./Interactions";
import { builder } from "./global";

export class StaticMesh {
    name = "";
    meshes: Object3D[] = [];
    group!: Group;
    pivot?: Group;
    nodes: Record<string, Object3D> = {};
    transforms: Record<
        string,
        {
            object: Object3D;
            position: Vector3;
            rotation: THREE.Euler;
            scale: Vector3;
        }
    > = {};
    index: number;
    type: AssetType;
    offset: number;
    collisionEnvelop: { obb: Mesh[]; sphere: Mesh[] } = { obb: [], sphere: [] };
    collisiongroup: Group = new Group();
    size: { width: number; height: number; depth: number } = {
        width: 0,
        height: 0,
        depth: 0,
    };

    constructor(type: AssetType, offset = 0) {
        this.group = new Group();
        this.offset = offset;
        this.type = type;
        this.meshes = [];
        this.index = 0;

        this.nodes = {};
        this.transforms = {};
    }
    setMeshes(meshes: Mesh[]): void {
        //this.meshes = meshes;
        meshes.forEach((mesh) => {
            this.group.add(mesh);
        });
    }
    setGroup(gr: Group): void {
        this.group = gr;
        this.meshes = [];

        this.nodes = {};
        this.transforms = {};

        this.group.traverse((child: Object3D) => {
            if (child.name) {
                const key = child.name.toLowerCase();

                this.nodes[key] = child;

                this.transforms[key] = {
                    object: child,
                    position: child.position.clone(),
                    rotation: child.rotation.clone(),
                    scale: child.scale.clone(),
                };
            }

            if (child instanceof THREE.Mesh) {
                child.userData = {
                    ...child.userData,
                    type: this.type,
                    staticmesh: this,
                };

                this.meshes.push(child);
            }
        });
    }
    clone(): StaticMesh {
        this.group.updateMatrix();
        this.group.updateMatrixWorld();
        const nSM = new StaticMesh(this.type);
        this.meshes.forEach((mesh) => {
            const m = new Mesh(mesh.geometry, mesh.material);
            m.castShadow = mesh.castShadow;
            m.receiveShadow = mesh.receiveShadow;
            m.frustumCulled = mesh.frustumCulled;
            nSM.addMesh(m);
        });

        nSM.group.applyMatrix4(this.group.matrixWorld.clone());
        this.index++;
        nSM.name = this.name + " - " + this.index;
        return nSM;
    }
    setPosition(pos: Vector3, room: Room | undefined, isOffset = true): boolean {
        const currentPos = this.group.position.clone();
        if (isOffset) pos.y = this.offset;
        /* if (this.type === AssetType.Stack) {
            const alpha = pos.x >= 0 ? 0 : 180;
            this.setRotationY(alpha, undefined);
        }
        if (this.type === AssetType.Console) {
            const alpha = pos.z >= 0 ? 0 : 180;
            this.setRotationY(alpha, undefined);
        } */
        this.setCollisionPosition(pos);
        GraphicService.highlightObject(this, "#00FF00");
        if (room)
            if (
                this.checkCollisionEquipement(room) > 0 ||
                this.checkCollisionBSU(room) > 0 ||
                checkCollisionWalls(this) > 0
            ) {
                this.setCollisionPosition(currentPos);
                return false;
            }
        this.group.position.copy(pos);
        this.group.updateMatrixWorld(true);
        if (room && this.type === AssetType.Trocar) {
            const colorName = room.trocarToColor[this.name];
            moveBSU(colorName);
        }
        return true;
    }
    translate(p: Vector3, room: Room | undefined): void {
        this.group.position.add(p);
        if (room) {
            return;
        }
        /* const Pos = this.group.position.clone();
        Pos.add(p);
        this.setPosition(Pos, room);  */
    }
    private setCollisionPosition(pos: Vector3): void {
        this.collisiongroup.position.copy(pos);
        this.collisiongroup.updateMatrixWorld(true);
        this.collisiongroup.traverse((child) => {
            if (child instanceof Mesh) {
                child.updateMatrix();
                child.updateMatrixWorld();
                if (child.geometry.userData.obb !== undefined) {
                    const obb = child.userData.obb as OBB;

                    obb.copy(child.geometry.userData.obb);
                    obb.applyMatrix4(child.matrixWorld);
                }
            }
        });
    }
    private checkCollisionEquipement(room: Room): CollisionType {
        if (room.SM_stack) {
            if (this !== room.SM_stack)
                if (checkCollision(this, room.SM_stack) > 0) {
                    return CollisionType.Collision;
                }
        }
        if (room.SM_trolley) {
            if (this !== room.SM_trolley)
                if (checkCollision(this, room.SM_trolley) > 0) {
                    return CollisionType.Collision;
                }
        }
        if (room.SM_anesthesia) {
            if (this !== room.SM_anesthesia)
                if (checkCollision(this, room.SM_anesthesia) > 0) {
                    return CollisionType.Collision;
                }
        }
        if (room.SM_surgeon) {
            if (this !== room.SM_surgeon)
                if (checkCollision(this, room.SM_surgeon) > 0) {
                    return CollisionType.Collision;
                }
        }
        if (room.SM_slave) {
            if (this !== room.SM_slave)
                if (checkCollision(this, room.SM_slave) > 0) {
                    return CollisionType.Collision;
                }
        }
        if (room.SM_console) {
            if (this !== room.SM_console) {
                const isCollision = checkCollision(this, room.SM_console);
                if (isCollision > 0) {
                    return CollisionType.Collision;
                }
            }
        }
        if (room.SM_bed) {
            if (this !== room.SM_bed) {
                if (checkBVHCollision(this, room.SM_bed)) {
                    return CollisionType.Collision;
                }
            }
        }
        if (room.SM_patient) {
            if (this !== room.SM_patient) {
                if (checkBVHCollision(this, room.SM_patient)) {
                    return CollisionType.Collision;
                }
            }
        }
        return CollisionType.NoCollision;
    }
    private checkCollisionBSU(room: Room): CollisionType {
        if (room.SM_vbsu) {
            for (const key in room.SM_vbsu) {
                if (key !== this.name) {
                    if (checkCollision(this, room.SM_vbsu[key]) > 0) {
                        return CollisionType.Collision;
                    }
                }
            }
        }
        if (room.SM_ibsu) {
            for (const key in room.SM_ibsu) {
                if (key !== this.name)
                    if (checkCollision(this, room.SM_ibsu[key]) > 0) {
                        return CollisionType.Collision;
                    }
            }
        }
        return CollisionType.NoCollision;
    }
    setTransform(transform: { pos: Vector3; rot: number }): void {
        this.setPosition(transform.pos, undefined);
        this.setRotation(transform.rot, undefined);
    }

    setOrientation(targetOr: Vector3): void {
        const q = new Quaternion().setFromUnitVectors(new Vector3(0.0, -1.0, 0.0), targetOr);
        q.normalize();
        this.setWorldQuaternion(q);
    }

    setWorldPosition(position: Vector3): void {
        const parent = this.group.parent;

        if (parent) {
            parent.updateWorldMatrix(true, false);
            this.group.position.copy(parent.worldToLocal(position.clone()));
        } else {
            this.group.position.copy(position);
        }

        this.group.updateMatrixWorld(true);
    }

    setWorldQuaternion(quaternion: Quaternion): void {
        const parent = this.group.parent;

        if (parent) {
            parent.updateWorldMatrix(true, false);

            const parentWorldQuaternion = parent.getWorldQuaternion(new Quaternion());
            const localQuaternion = parentWorldQuaternion.invert().multiply(quaternion);

            this.group.quaternion.copy(localQuaternion).normalize();
        } else {
            this.group.quaternion.copy(quaternion).normalize();
        }

        this.group.updateMatrixWorld(true);
    }

    setRotationX(alpha: number): void {
        const alphaRad = MathUtils.degToRad(alpha);
        const oldValue = this.group.rotation.x;
        this.group.rotation.x = alphaRad;
        if (this.type === AssetType.Trocar) {
            if (builder.room.SM_patient && !this.isDepthValid(builder.room.SM_patient)) {
                this.group.rotation.x = oldValue;
                return;
            }
            const colorname = builder.room.trocarToColor[this.name];
            const bsu = moveBSU(colorname);
            bsu?.rotatePort();
        }
    }
    setRotationY(alpha: number, room: Room | undefined = undefined): void {
        const alphaRad = MathUtils.degToRad(alpha);
        const oldValue = this.group.rotation.z;
        if (this.type === AssetType.Trocar) {
            this.group.rotation.z = alphaRad;
            if (builder.room.SM_patient && !this.isDepthValid(builder.room.SM_patient)) {
                this.group.rotation.z = oldValue;
                return;
            }
            const colorname = builder.room.trocarToColor[this.name];
            const bsu = moveBSU(colorname);
            bsu?.rotatePort();
        } else {
            this.setRotation(alphaRad, room);
        }
    }

    public setNodeTransform(
        target: string,
        property: "position" | "rotation" | "scale",
        axis: "x" | "y" | "z",
        value: number
    ): void {
        if (!target) {
            console.error("setNodeTransform: Missing target");
            return;
        }

        const nodeName = target.toLowerCase();

        const node = this.nodes[nodeName];

        if (!node) {
            console.error(
                `setNodeTransform: Node "${nodeName}" not found`,
                Object.keys(this.nodes)
            );

            return;
        }

        switch (property) {
            case "position":
                node.position[axis] = value;
                break;

            case "rotation":
                node.rotation[axis] = MathUtils.degToRad(value);
                break;

            case "scale":
                node.scale[axis] = value;
                break;
        }

        const storedTransform = this.transforms[nodeName];

        if (storedTransform) {
            storedTransform.position.copy(node.position);
            storedTransform.rotation.copy(node.rotation);
            storedTransform.scale.copy(node.scale);
        }

        node.updateMatrixWorld(true);
    }

    setRotationZ(alpha: number): void {
        const alphaRad = MathUtils.degToRad(alpha);
        const oldValue = this.group.rotation.z;
        this.group.rotation.z = alphaRad;

        if (this.type === AssetType.Trocar) {
            if (builder.room.SM_patient && !this.isDepthValid(builder.room.SM_patient)) {
                this.group.rotation.z = oldValue;
                return;
            }
            const colorname = builder.room.trocarToColor[this.name];
            moveBSU(colorname);
        }
    }
    getRotationX(): number {
        this.group.updateMatrixWorld();
        return MathUtils.radToDeg(this.group.rotation.x);
    }
    getRotationY(): number {
        this.group.updateMatrixWorld();
        return MathUtils.radToDeg(this.group.rotation.y);
    }
    getRotationZ(): number {
        this.group.updateMatrixWorld();
        return MathUtils.radToDeg(this.group.rotation.z);
    }

    getPosition(): Vector3 {
        return this.group.position;
    }
    getTransform(): ITransform {
        const transform: ITransform = { position: [], rotation: 0 };
        this.group.position.toArray(transform.position);
        transform.rotation = MathUtils.radToDeg(this.group.rotation.y);
        transform.rotation = MathUtils.radToDeg(this.group.rotation.y);
        return transform;
    }
    addMesh(mesh: Mesh): void {
        mesh.userData = {
            ...mesh.userData,
            type: this.type,
            staticmesh: this,
        };

        this.group.add(mesh);
        this.meshes.push(mesh);
    }

    getDimension(): { width: number; height: number; depth: number } {
        const box = new Box3().setFromObject(this.group);
        const s = new Vector3();
        box.getSize(s);
        s.multiplyScalar(0.5);
        this.size = {
            width: s.x,
            height: s.y,
            depth: s.z,
        };
        return this.size;
    }
    setCollisionEnvolop(collisionEnvelop: { obb: Mesh[]; sphere: Mesh[] }): void {
        this.collisionEnvelop = collisionEnvelop;
        this.collisionEnvelop.obb.forEach((obb) => {
            this.collisiongroup.add(obb);
        });
        this.collisionEnvelop.sphere.forEach((sph) => {
            this.collisiongroup.add(sph);
        });
        const b = new Box3().setFromObject(this.collisiongroup);
        this.collisiongroup.userData["box"] = b;
    }

    addCollisionEnvelop(collisionObject: Mesh, isOBB = true): void {
        this.collisiongroup.add(collisionObject);
        isOBB
            ? this.collisionEnvelop.obb.push(collisionObject)
            : this.collisionEnvelop.sphere.push(collisionObject);
        /* if (collisionObject instanceof OBB){
                this.collisionEnvelop.obb.push(collisionObject);
            }
            else {
                this.collisionEnvelop.sphere.push(collisionObject);
            } */
    }

    dispose(): void {
        this.meshes.forEach((obj) => {
            obj.geometry.dispose();
        });
        this.collisiongroup.children.forEach((obj) => {
            if (obj instanceof Mesh) {
                obj.geometry.dispose();
            }
        });
        this.group.clear();
    }

    moveTo(p: Vector3): void {
        this.group.position.copy(p);
    }
    /**
     * check if there is enough space for the instrument
     * @returns true if the distance between the port and the opposite surface is enough to host the instrument, false otherwise.
     */
    isDepthValid(patient: StaticMesh): boolean {
        if (this.type !== AssetType.Trocar) return true;

        this.group.updateWorldMatrix(true, false);
        patient.group.updateWorldMatrix(true, true);

        const worldQuaternion = this.group.getWorldQuaternion(new Quaternion());
        const Yaxis = new Vector3(0.0, 0.0, 1.0);
        const Xaxis = new Vector3(1.0, 0.0, 0.0);
        const Zaxis = new Vector3(0.0, -1.0, 0.0);
        Zaxis.applyQuaternion(worldQuaternion).normalize();
        Xaxis.applyQuaternion(worldQuaternion).normalize();
        Yaxis.applyQuaternion(worldQuaternion).normalize();
        const instrumentRadius = 0.0085;
        const pos = new Vector3()
            .copy(this.group.getWorldPosition(new Vector3()))
            .add(new Vector3().copy(Zaxis).multiplyScalar(0.05));
        const vertices = [
            pos,
            new Vector3().addVectors(
                pos,
                new Vector3().copy(Xaxis).multiplyScalar(instrumentRadius)
            ),
            new Vector3().addVectors(
                pos,
                new Vector3().copy(Xaxis).multiplyScalar(-instrumentRadius)
            ),
            new Vector3().addVectors(
                pos,
                new Vector3().copy(Yaxis).multiplyScalar(instrumentRadius)
            ),
            new Vector3().addVectors(
                pos,
                new Vector3().copy(Yaxis).multiplyScalar(-instrumentRadius)
            ),

            new Vector3().addVectors(
                pos,
                new Vector3().addVectors(Xaxis, Yaxis).normalize().multiplyScalar(instrumentRadius)
            ),
            new Vector3().addVectors(
                pos,
                new Vector3().addVectors(Xaxis, Yaxis).normalize().multiplyScalar(-instrumentRadius)
            ),
            new Vector3().addVectors(
                pos,
                new Vector3().subVectors(Xaxis, Yaxis).normalize().multiplyScalar(instrumentRadius)
            ),

            new Vector3().addVectors(
                pos,
                new Vector3().subVectors(Xaxis, Yaxis).normalize().multiplyScalar(-instrumentRadius)
            ),
        ];
        const raycaster = new Raycaster();
        let result = true;
        let intersect = false;
        for (const vertex of vertices) {
            raycaster.set(vertex, Zaxis);
            const intersects = raycaster.intersectObjects(patient.meshes, true);
            if (intersects.length > 0) {
                intersect = true;
                if (intersects[0].distance < 0.145) {
                    result = false;
                    break;
                }
            }
        }
        if (!intersect) return false;
        else return result;
    }

    rotate(alpha: number): void {
        this.group.rotation.y += alpha;
    }

    setRotation(alpha: number, room: Room | undefined): void {
        const currentAlpha = this.group.rotation.y;

        this.setCollisionRotation(alpha);
        if (room) {
            if (
                this.checkCollisionEquipement(room) > 0 ||
                this.checkCollisionBSU(room) > 0 ||
                checkCollisionWalls(this) > 0
            ) {
                this.setCollisionRotation(currentAlpha);
                return;
            }
        }
        this.group.rotation.y = alpha;
    }

    /**
     *
     * Bed Rotation is applied to the waist node of the bed mesh, which is a child of the group.
     * The rotation is applied around the Y-axis.
     */
    setBedRotationY(alpha: number): void {
        const waist = this.group.getObjectByName("waist");

        if (!waist) {
            console.warn("Bed waist node not found");
            return;
        }

        waist.rotation.y = MathUtils.degToRad(alpha);
        waist.updateMatrixWorld(true);
    }

    private setCollisionRotation(alpha: number): void {
        this.collisiongroup.rotation.y = alpha;
        this.collisiongroup.updateMatrixWorld(true);
        this.collisiongroup.traverse((child) => {
            if (child instanceof Mesh) {
                child.updateMatrix();
                child.updateMatrixWorld();
                if (child.geometry.userData.obb !== undefined) {
                    (child.userData.obb as OBB).copy(child.geometry.userData.obb);
                    child.userData.obb.applyMatrix4(child.matrixWorld);
                }
            }
        });
    }
    checkCollision(object: StaticMesh | Robot): boolean | undefined {
        if (object instanceof StaticMesh) {
            for (const obb of object.collisionEnvelop.obb) {
                for (const obb1 of this.collisionEnvelop.obb) {
                    if (obb.userData.obb.intersectsOBB(obb1.userData.obb)) return true;
                }
                for (const sph of this.collisionEnvelop.sphere) {
                    if (obb.userData.obb.intersectsSphere(sph.userData.sph)) return true;
                }
            }

            for (const sph of object.collisionEnvelop.sphere) {
                for (const obb1 of this.collisionEnvelop.obb) {
                    if (obb1.userData.obb.intersectsOBB(sph.userData.sph)) return true;
                }
                for (const sph1 of this.collisionEnvelop.sphere) {
                    if (sph.userData.sph.intersectsSphere(sph1.userData.sph)) return true;
                }
            }
            return false;
        } else {
            const obb = object.base.userData.obb as OBB;
            for (const obb1 of this.collisionEnvelop.obb) {
                if (obb.intersectsOBB(obb1.userData.obb) === true) return true;
            }
            for (const sph of this.collisionEnvelop.sphere) {
                if (obb.intersectsSphere(sph.userData.sph)) return true;
            }
        }
    }
}
